import type { GridApi } from 'ag-grid-community';

export interface ExportColumn {
  field: string;
  headerName: string;
}

/**
 * Escape a value for safe inclusion in a CSV cell.
 * Handles null/undefined, commas, quotes, and newlines.
 */
export function escapeCsvValue(value: unknown): string {
  if (value === null || value === undefined) return '';

  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return value.toString();

  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Generate a CSV string from row data and column definitions.
 */
export function generateCsv(rows: Record<string, unknown>[], columns: ExportColumn[]): string {
  const header = columns.map((col) => escapeCsvValue(col.headerName)).join(',');
  const dataRows = rows.map((row) =>
    columns.map((col) => escapeCsvValue(row[col.field])).join(',')
  );
  return [header, ...dataRows].join('\n');
}

/**
 * Trigger a browser download of a CSV string.
 */
export function downloadCsv(filename: string, csvContent: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Read the currently visible columns from an AG Grid instance.
 *
 * @param gridApi - The AG Grid API
 * @param computedColumnMap - Optional map of headerName -> virtual field name
 *   for computed columns that don't have a `colId` matching a data field
 *   (e.g. { "Disc'd Price": "_discountedPrice" }).
 */
export function getVisibleColumnsFromGrid(
  gridApi: GridApi,
  computedColumnMap?: Record<string, string>
): ExportColumn[] {
  const columnState = gridApi.getColumnState();
  const columns: ExportColumn[] = [];

  for (const colState of columnState) {
    if (colState.hide) continue;

    const colDef = gridApi.getColumn(colState.colId)?.getColDef();
    if (!colDef) continue;

    const headerName = (colDef.headerName as string) || colState.colId;

    // Skip the checkbox selection column
    if (!colDef.headerName && !colDef.field) continue;

    // Check if this is a computed column
    if (computedColumnMap && computedColumnMap[headerName]) {
      columns.push({ field: computedColumnMap[headerName], headerName });
    } else if (colDef.field) {
      columns.push({ field: colDef.field, headerName });
    }
  }

  return columns;
}
